import type { JobSalary } from "@lossdog/job-watchlists";
import { useCallback, useEffect, useState } from "react";

import { useJobSalaryContext } from "@/data/watchlist/JobSalary.context";

interface UseJobDataReturn {
  jobData: JobSalary | null;
  isLoading: boolean;
  error: string | null;

  // Extracted information from job ID
  jobSubject: string | null;
  metroAreaId: string | null;
  quantileId: string | null;

  // Refetch function
  refetch: () => void;
}

interface ParsedJobId {
  jobSubject: string;
  metroAreaId: string;
  quantileId: string;
}

/**
 * Parses a job ID and extracts its components
 * Format: job-salary.{jobSubject}.{metroAreaId}.{quantileId}
 * Example: job-salary.Business-Development-Analyst.16980.a_pct10
 */
const parseJobId = (jobId: string): ParsedJobId | null => {
  const parts = jobId.split(".");

  // Validate format: should have 4 parts starting with "job-salary"
  if (parts.length !== 4 || parts[0] !== "job-salary") {
    return null;
  }

  const [, jobSubject, metroAreaId, quantileId] = parts;

  // Validate that all parts are present
  if (!jobSubject || !metroAreaId || !quantileId) {
    return null;
  }

  return {
    jobSubject,
    metroAreaId,
    quantileId,
  };
};

/**
 * Hook to get job data by job ID
 *
 * @param jobId - The job ID in format: job-salary.{jobSubject}.{metroAreaId}.{quantileId}
 * @returns Job data, loading state, error, and extracted components
 */
export const useJobData = (jobId: string | null): UseJobDataReturn => {
  const { client, isLoading: contextLoading } = useJobSalaryContext();

  const [jobData, setJobData] = useState<JobSalary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Parse job ID to extract components
  const parsedJobId = jobId ? parseJobId(jobId) : null;

  const fetchJobData = useCallback(async () => {
    if (!jobId || !client || !parsedJobId) {
      setJobData(null);
      setError(jobId ? "Invalid job ID format" : null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { jobSubject, metroAreaId, quantileId } = parsedJobId;

      // Get metro area and quantile from client
      const metroArea = client.getMetroAreas().find((area) => area.id === metroAreaId);
      const quantile = client.getQuantiles().find((q) => q.id === quantileId);

      if (!metroArea) {
        throw new Error(`Metro area not found: ${metroAreaId}`);
      }

      if (!quantile) {
        throw new Error(`Quantile not found: ${quantileId}`);
      }

      const existingJob = client.getOrCreateJobSalary(jobSubject, metroArea, quantile);
      existingJob.subscribe();
      setJobData(existingJob);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch job data";
      setError(errorMessage);
      setJobData(null);
    } finally {
      setIsLoading(false);
    }
  }, [client, jobId, parsedJobId]);

  // Fetch job data when dependencies change
  useEffect(() => {
    if (contextLoading) return;

    fetchJobData();
  }, [fetchJobData, contextLoading]);

  const refetch = useCallback(() => {
    fetchJobData();
  }, [fetchJobData]);

  return {
    jobData,
    isLoading: isLoading || contextLoading,
    error,

    // Extracted components
    jobSubject: parsedJobId?.jobSubject || null,
    metroAreaId: parsedJobId?.metroAreaId || null,
    quantileId: parsedJobId?.quantileId || null,

    refetch,
  };
};
